import React, { useState, useContext } from "react";
import Select from "react-select";
import css from "./CarSearch.module.css";
import DateSelect from "../../../UI/Inputs/DateSelect";
import { AppContext } from "../../../../providers/appProvider";
import { selectStyle } from "./selectStyle";
import Search from "../../../../assets/Icon/Search";
import User from "../../../../assets/Icon/User";
import { useAuth } from "../../../../providers/authProvider";
import DateSelectRange from "../../../UI/Inputs/DateSelectRange";
import { useTranslation } from 'react-i18next';

const carFilterSelectStyle = {
  ...selectStyle,
  control: (provided, state) => ({
    ...selectStyle.control(provided, state),
    minWidth: 0,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 10000,
  }),
};

const CarSearch = ({ carService, isTable, columns }) => {
  const {t} = useTranslation();
  const { appState, app } = useContext(AppContext);
  const [currentTab, setCurrentTab] = useState(1);
  const { isRole } = useAuth(AppContext);

  const countries = appState.countries.map((country) => ({
    value: country.id,
    label: country.name,
  }));
  const websites = appState.websites.map((website) => ({
    value: website.id,
    label: website.name,
  }));
  
  const dealers = appState.dealers.map((user) => ({
    value: user.id,
    label: user.firstname + " " + user.lastname,
  }));

  const salers = appState.salers.map((user) => ({
    value: user.id,
    label: user.firstname + " " + user.lastname,
  }));
  const statuses = appState.statuses.map((status) => ({
    value: status.id,
    label: status.name,
  }));
  const marines = appState.marines.map((marine) => ({
    value: marine.id,
    label: marine.name,
  }));
  const years = Array.from(
    { length: new Date().getFullYear() - 1919 },
    (_, index) => {
      const year = new Date().getFullYear() - index;
      return { value: year, label: year.toString() };
    }
  );
  const makers = appState.makers.map((maker) => ({
    value: maker.id,
    label: maker.name,
  }));
  const models = appState.models
    .filter((model) => model.maker_id === carService.filter.maker_id)
    .map((model) => ({
      value: model.id,
      label: model.name,
    }));

  const insurances = [
    { value: 1, label: "--" },
    { value: 2, label: t("Insured") },
    { value: 3, label: t("NotInsured") },
  ];

  const sources = [
    { value: 4, label: t('Bitrix') },
    { value: 2, label: t('CalledCity') },
    { value: 1, label: t('AutovinSite')},
    { value: 3, label: t('CameToOffice') },
    { value: 7, label: t('OldClient') },
    { value: 5, label: t('Recommendation') },
    { value: 6, label: t('OutdoorAd') },
    { value: 8, label: t('Unknown') },
  ];

  const customFilers = [
    { value: 1, label: t('ClientDebt') },
    { value: 2, label: t('CompanyDebt') },
    { value: 3, label: t('Problematic') },
    { value: 4, label: t('PlannedProfit') },
    { value: 5, label: t('Bonus') },
    { value: 6, label: t('PayableBonus') },
    { value: 7, label: t('PaidBonus') },
  ];

  const loadingFilers = [
    { value: 1, label: t('Uploaded') },
    { value: 2, label: t('ToBeUploaded') }, 
  ];

  const tabs = [{ value: 1, label: t('Active') }];

  const discountOptions = [
    { value: 1, label: t('All') },
    { value: 2, label: t('Discounted') },
  ];

  const dealerOptions = [
    { value: 1, label: t('All') },
    { value: 2, label: t('Filled') },
    { value: 3, label: t('Descending')},
    { value: 4, label: t('Ascending') },
  ]

  const percentOptions = [
    { value: 1, label: t('Funded') },  
    { value: 2, label: t('ToBeFunded')},
    { value: 3, label: t('Problem') },
  ];

  if (app.hasPermission("FINANCE_ARCHIVE_SHOW")) {
    tabs.push({ value: 2, label: t('Archive') });
  }

  if (app.hasPermission("FINANCE_DELETED_SHOW")) {
    tabs.push({ value: 3, label: t('Deleted') });
  }

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    carService.setFilter({
      ...carService.filter,
      [name]: value,
      page: 1,
    });
  };

  const onChangeTab = (val) => {
    setCurrentTab(val.value);
    switch (val.value) {
      case 1:
        carService.setFilter((prevState) => ({
          ...prevState,
          is_archive: false,
          is_deleted: false,
          page: 1,
        }));
        break;
      case 2:
        carService.setFilter((prevState) => ({
          ...prevState,
          is_archive: true,
          is_deleted: false,
          page: 1,
        }));
        break;
      case 3:
        carService.setFilter((prevState) => ({
          ...prevState,
          is_archive: false,
          is_deleted: true,
          page: 1,
        }));
        break;
      default:
        carService.setFilter((prevState) => ({
          ...prevState,
          is_archive: false,
          is_deleted: false,
          page: 1,
        }));
    }
  };

  const handleOptionChange = (selectedOption, name) => {
    carService.setFilter({
      ...carService.filter,
      [name]: selectedOption ? selectedOption.value : 0,
      page: 1,
    });
  };

  const hanldePurchasedMin = (date) => {
    carService.setFilter({
      ...carService.filter,
      purchased_at_min: date ? date : "",
      page: 1,
    });
  };

  const hanldePurchasedMax = (date) => {
    carService.setFilter({
      ...carService.filter,
      purchased_at_max: date ? date : "",
      page: 1,
    });
  };

  const hanldePurchasedMinMax = (startDate, endDate) => {
    carService.setFilter({
      ...carService.filter,
      purchased_at_min: startDate ? startDate : "",
      purchased_at_max: endDate ? endDate : "",
    });
  };

  const hanldeSenderedMinMax = (startDate, endDate) => {
    carService.setFilter({
      ...carService.filter,
      sender_at_min: startDate ? startDate : "",
      sender_at_max: endDate ? endDate : "",
    });
  };

  const handleBonusDoneMinMax = (startDate, endDate) => {
    const adjustedEndDate = endDate ? `${endDate.split(" ")[0]} 23:59:59` : "";

    carService.setFilter({
      ...carService.filter,
      bonus_done_date_min: startDate || "",
      bonus_done_date_max: adjustedEndDate,
    });
  };

  var isTableVar = isTable;

  const [isActive, setIsActive] = useState(false);

  const handleSearchClick = () => {
    setIsActive(!isActive);
  };

  if (!isTableVar) {
    return (
      <>
        <div className={css.searchBTN} onClick={handleSearchClick}>
          <span className={css.icon}>
            {" "}
            <Search />
          </span>
          <span>{t('Search')}</span>
        </div>

        <div
          className={
            isActive ? css.overlayBG + " " + css.active : css.overlayBG
          }
          onClick={handleSearchClick}
        ></div>

        <div
          className={
            isActive ? css.searchBox + " " + css.active : css.searchBox
          }
        >
          <div className={css.col + " " + css.colText}>
            <div className={css.groupInput}>
              <div className={css.groupInputIcon}>
                <input
                  name="search_query"
                  className={css.input}
                  placeholder="ID, VIN, Lot ..."
                  value={carService.filter.search_query}
                  onChange={handleInputChange}
                />
                <div className={css.crmIcon}>
                  <Search />
                </div>
              </div>
            </div>
          </div>

          <div className={css.col + " " + css.colText}>
            <div className={css.groupInput}>
              <div className={css.groupInputIcon}>
                <input
                  name="client_query"
                  className={css.input}
                  placeholder={ t('Firstname') + " " + t('Lastname')}
                  value={carService.filter.client_query}
                  onChange={handleInputChange}
                />
                <div className={css.crmIcon}>
                  <User />
                </div>
              </div>
            </div>
          </div>

          <div className={css.col + " " + css.colText}>
            <div className={css.dateGroup + " twomonth"}>
              <DateSelectRange
                defDate={""}
                onChange={hanldePurchasedMinMax}
                placeholderText={t('PurchaseDate')}
                popperClassName={css.dataContainer}
                wrapperClassName={css.dataContainer}
                className={css.input}
                isClearable
              />
            </div>
          </div>
          
          {app.hasPermission("SALER_FILTER_SEARCH") && (
            <div className={css.col}>
              <div className={css.inBox}>
                <Select
                  styles={selectStyle}
                  options={salers}
                  value={salers.find(
                    (option) => option.value === carService.filter.saler_id
                  )}
                  onChange={(selectedOption) => {
                    carService.setFilter((prevState) => ({
                      ...prevState,
                      saler_id: selectedOption ? selectedOption.value : 0,
                      page: 1,
                    }));
                  }}
                  isClearable
                  placeholder={t('Saler')}
                />
              </div>
            </div>
          )}

          {app.hasPermission("DEALER_FILTER_SEARCH") && (

            <div className={css.col}>
              <div className={css.inBox}>
                <Select
                  styles={selectStyle}
                  options={dealers}
                  value={dealers.find(
                    (option) => option.value === carService.filter.dealer_id
                  )}
                  onChange={(selectedOption) => {
                    carService.setFilter((prevState) => ({
                      ...prevState,
                      dealer_id: selectedOption ? selectedOption.value : 0,
                      page: 1,
                    }));
                  }}
                  isClearable
                  placeholder={t('Dealer')}
                />
              </div>
            </div>

          )}


          <div className={css.col}>
            <div className={css.inBox}>
              <Select
                styles={selectStyle}
                options={websites}
                value={websites.find(
                  (option) => option.value === carService.filter.website_id
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    website_id: selectedOption ? selectedOption.value : 0,
                    page: 1,
                  }));
                }}
                isClearable
                placeholder={t('WebSite')}
              />
            </div>
          </div>

          {app.hasPermission("DEBT_FILTER_SEARCH") && (
            <div className={css.col}>
              <div className={css.inBox}>
                <Select
                  styles={selectStyle}
                  options={customFilers}
                  value={customFilers.find(
                    (option) => option.value === carService.filter.custom_filter
                  )}
                  onChange={(selectedOption) => {
                    carService.setFilter((prevState) => ({
                      ...prevState,
                      custom_filter: selectedOption ? selectedOption.value : 0,
                      page: 1,
                    }));
                  }}
                  isClearable
                  placeholder={t('Debt')}
                />
              </div>
            </div>
          )}

          {app.hasPermission("CONTAINER_EDIT") && (
            <div className={css.col + " " + css.colSmall}>
              <div className={css.inBox}>
                <Select
                  styles={selectStyle}
                  options={loadingFilers}
                  value={loadingFilers.find(
                    (option) =>
                      option.value === carService.filter.loading_filter
                  )}
                  onChange={(selectedOption) => {
                    carService.setFilter((prevState) => ({
                      ...prevState,
                      loading_filter: selectedOption ? selectedOption.value : 0,
                      page: 1,
                    }));
                  }}
                  isClearable
                  placeholder={t('Loading')}
                />
              </div>
            </div>
          )}

          {app.hasPermission("STATUS_FILTER_SEARCH") && (
            <div className={css.col}>
              <div className={css.inBox}>
                <Select
                  styles={selectStyle}
                  options={tabs}
                  value={tabs.find((option) => option.value === currentTab)}
                  onChange={onChangeTab}
                />
              </div>
            </div>
          )}
        </div>
      </>
    );
  } else {
    return (
      <thead style={{ zIndex: "1" }}>
        <tr>
          <th
            className={css.pad0 + " " + css.id + " ---- " + css.stickyCol}
            style={{
              zIndex: "999",
              position: "sticky",
              left: "0px",
              backgroundColor: "#f2f2f2 !important",
            }}
          ></th>

          {columns.id && (
            <th
              className={
                css.pad0 + " " + css.id + " --- " + css.stickyColSecond
              }
              style={{
                zIndex: "999",
                position: "sticky",
                left: "33px",
                backgroundColor: "#f2f2f2",
              }}
            >
              <input
                style={{
                  minWidth: "67px",
                  textAlign: "center",
                  backgroundColor: "transparent",
                }}
                name="id"
                placeholder="#"
                value={carService.filter.id}
                onChange={handleInputChange}
              />
            </th>
          )}
          {columns.purchased && <th style={{ minWidth: "93px" }}>{t('Purchased')}</th>}
          {columns.sender_at && (
            <th style={{ minWidth: "93px" }}>{t('Sent')}</th>
          )}
          {columns.reciever_at && <th style={{ minWidth: "93px" }}>{t('Received')}</th>}

          {columns.website && (
            <th className={css.pad0}>
              <Select
                styles={selectStyle}
                options={websites}
                value={websites.find(
                  (option) => option.value === carService.filter.website_id
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    website_id: selectedOption ? selectedOption.value : 0,
                    page: 1,
                  }));
                }}
                isClearable
                placeholder={t('WebSite')}
              />
            </th>
          )}

          {app.hasPermission("CAR_AGENT_SHOW") && columns.agentcrm && <th style={{ minWidth: "110px" }}>{t('Agent')}</th>}

          {columns.export_country && (
            <th className={css.pad0}>
              <Select
                styles={selectStyle}
                options={countries}
                value={countries.find(
                  (option) =>
                    option.value === carService.filter.export_country_id
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    export_country_id: selectedOption
                      ? selectedOption.value
                      : 0,
                    page: 1,
                  }));
                }}
                isClearable
                placeholder={t('Export')}
              />
            </th>
          )}

          {columns.sender && (
            <th className={css.pad0}>
              <Select
                styles={selectStyle}
                options={countries}
                value={countries.find(
                  (option) =>
                    option.value === carService.filter.sender_country_id
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    sender_country_id: selectedOption
                      ? selectedOption.value
                      : 0,
                    page: 1,
                  }));
                }}
                isClearable
                placeholder={t('Sender')}
              />
            </th>
          )}

          {columns.reciever && (
            <th className={css.pad0}>
              <Select
                styles={selectStyle}
                options={countries}
                value={countries.find(
                  (option) =>
                    option.value === carService.filter.reciever_country_id
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    reciever_country_id: selectedOption
                      ? selectedOption.value
                      : 0,
                    page: 1,
                  }));
                }}
                placeholder={t('Reciever')}
                isClearable
              />
            </th>
          )}
          {columns.car && (
            <th className={css.pad0 + " " + css.carFilterCell}>
              <div className={css.carFilterGroup}>
                <div className={css.carFilterYear}>
                  <Select
                    styles={carFilterSelectStyle}
                    options={years}
                    value={
                      years.find(
                        (option) =>
                          option.value === carService.filter.prod_year
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      carService.setFilter((prevState) => ({
                        ...prevState,
                        prod_year: selectedOption ? selectedOption.value : 0,
                        page: 1,
                      }));
                    }}
                    isClearable
                    placeholder={t('Year')}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className={css.carFilterMake}>
                  <Select
                    styles={carFilterSelectStyle}
                    options={makers}
                    value={
                      makers.find(
                        (option) =>
                          option.value === carService.filter.maker_id
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      carService.setFilter((prevState) => ({
                        ...prevState,
                        maker_id: selectedOption ? selectedOption.value : 0,
                        model_id: 0,
                        page: 1,
                      }));
                    }}
                    isClearable
                    placeholder={t('Make')}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className={css.carFilterModel}>
                  <Select
                    styles={carFilterSelectStyle}
                    options={models}
                    value={
                      models.find(
                        (option) =>
                          option.value === carService.filter.model_id
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      carService.setFilter((prevState) => ({
                        ...prevState,
                        model_id: selectedOption ? selectedOption.value : 0,
                        page: 1,
                      }));
                    }}
                    isClearable
                    isDisabled={carService.filter.maker_id <= 0}
                    placeholder={t('Model')}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              </div>
            </th>
          )}
          {columns.vin && (
            <th className={css.pad0} style={{ minWidth: "155px" }}>
              <input
                name="vin"
                placeholder="VIN"
                value={carService.filter.vin}
                onChange={handleInputChange}
              />
            </th>
          )}
          {columns.client && (
            <th className={css.pad0}>
              <input
                name="client_query"
                placeholder={t('Client')}
                value={carService.filter.client_query}
                onChange={handleInputChange}
              />
            </th>
          )}
          {columns.contact && (
            <th className={css.pad0}>
              <input
                name="contact_query"
                placeholder={t('Contact')}
                value={carService.filter.contact_query}
                onChange={handleInputChange}
              />
            </th>
          )}
          {columns.saler && (
            <th className={css.pad0}>
              <Select
                styles={selectStyle}
                options={salers}
                value={salers.find(
                  (option) => option.value === carService.filter.saler_id
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    saler_id: selectedOption ? selectedOption.value : 0,
                    page: 1,
                  }));
                }}
                isClearable
                placeholder={t('Saler')}
              />
            </th>
          )}

          {columns.dealer && (
            <th className={css.pad0}>
              <Select
                styles={selectStyle}
                options={dealers}
                value={dealers.find(
                  (option) => option.value === carService.filter.dealer_id
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    dealer_id: selectedOption ? selectedOption.value : 0,
                    page: 1,
                  }));
                }}
                isClearable
                placeholder={t('Dealer')}
              />
            </th>
          )}

          {columns.bl_number && (
            <th className={css.pad0}>
              <input
                name="bl_number"
                placeholder="bl number"
                value={carService.filter.bl_number}
                onChange={handleInputChange}
              />
            </th>
          )}

          {columns.container && (
            <th className={"twomonth"} style={{ position: "relative" }}>
              <DateSelectRange
                style={{ minWidth: "150px" }}
                defDate={""}
                onChange={hanldeSenderedMinMax}
                placeholderText={t('SendAt')}
                popperClassName={css.dataContainer}
                wrapperClassName={css.dataContainer}
                className={css.input}
                isClearable
              />
            </th>
          )}

          {columns.container && (
            <th className={css.pad0}>
              <input
                name="container_number"
                placeholder={t('Container')}  
                value={carService.filter.container_number}
                onChange={handleInputChange}
              />
            </th>
          )}

          {columns.marine_line && (
            <th className={css.pad0}>
              <Select
                styles={selectStyle}
                options={marines}
                value={marines.find(
                  (option) => option.value === carService.filter.marine_line_id
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    marine_line_id: selectedOption ? selectedOption.value : 0,
                    page: 1,
                  }));
                }}
                isClearable
                placeholder={t('MarineLine')}
              />
            </th>
          )}

          {columns.transit && (
            <th className={css.pad0}>
              <input
                name="transit_number"
                placeholder={t('Transit')}
                value={carService.filter.transit_number}
                onChange={handleInputChange}
              />
            </th>
          )}

          {columns.insurance && app.hasPermission("INSURANCE_SHOW") && (
            <th className={css.pad0}>
              <Select
                styles={selectStyle}
                options={insurances}
                value={insurances.find(
                  (option) => option.value === carService.filter.insurance
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    insurance: selectedOption ? selectedOption.value : 0,
                    page: 1,
                  }));
                }}
                isClearable
                placeholder={t('Insurance')}
              />
            </th>
          )}

          {app.hasPermission("SOURCE_SELL_CHANNEL_SHOW") && (
            <th className={css.pad0}>
              <Select
                styles={selectStyle}
                options={sources}
                value={sources.find(
                  (option) => option.value === carService.filter.source_id
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    source_id: selectedOption ? selectedOption.value : 0,
                    page: 1,
                  }));
                }}
                isClearable
                placeholder={t('Source')}
              />
            </th>
          )}

          {app.hasPermission("CLIENT_PRICE_SHOW") && (
            <th>{t('ClientPrice')}</th>
          )}

          {app.hasPermission("SALER_PRICE_SHOW") && (
            <th>{t('SalerPrice')}</th>
          )}

          {app.hasPermission("CLIENT_PAID_SHOW") && <th>{t('ClientPaid')}</th>}
          {app.hasPermission("DEPOSIT_SHOW") && <th>{t('Deposit')}</th>}

          {/* {app.hasPermission("DEALER_PRICE_SHOW") && <th>დილერესი მომს.</th>} */}

          {app.hasPermission("DEALER_PRICE_SHOW") && (
            <th className={css.pad0}>
              <Select
                styles={selectStyle}
                options={dealerOptions}
                value={dealerOptions.find(
                  (option) => option.value === carService.filter.dealer_option_id
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    dealer_option_id: selectedOption ? selectedOption.value : 0,
                    page: 1,
                  }));
                }}
                isClearable
                placeholder={t('DealerCommission')}
              />
            </th>
          )}

          {columns.client_discount && app.hasPermission("CLIENT_DISCOUNT") && (
            <th className={css.pad0}>
              <Select
                styles={selectStyle}
                options={discountOptions}
                value={discountOptions.find(
                  (option) => option.value === carService.filter.discount_id
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    discount_id: selectedOption ? selectedOption.value : 0,
                    page: 1,
                  }));
                }}
                isClearable
                placeholder={t('ClientDiscount')}
              />
            </th>
          )}

          {app.hasPermission("CLIENT_DEBT_SHOW") && (
            <th>{t('ClientDebt')}</th>
          )}

          {app.hasPermission("COMPANY_PRICE_SHOW") && (
            <th>{t('CompanyPrice')}</th>
          )}
          {app.hasPermission("COMPANY_PAID_SHOW") && (
            <th>{t('CompanyPaid')}</th>
          )}

          {app.hasPermission("COMPANY_DEBT_SHOW") && (
            <th>{t('CompanyDebt')}</th>
          )}

          {app.hasPermission("BONUS_SHOW") && (
            <>
              <th>{t('Bonus')}</th>

              {app.hasPermission("BONUS_DONE") && (
                <th className={"twomonth"} style={{ position: "relative" }}>
                  <DateSelectRange
                    style={{ minWidth: "150px" }}
                    defDate={""}
                    onChange={handleBonusDoneMinMax}
                    placeholderText={t('BonusDate')}
                    popperClassName={css.dataContainer}
                    wrapperClassName={css.dataContainer}
                    className={css.input}
                    isClearable
                  />
                </th>
              )}
            </>
          )}

          {columns.percent && app.hasPermission("FUND_CALC_SHOW") && (
            <th className={css.pad0}>
              <Select
                styles={selectStyle}
                options={percentOptions}
                value={percentOptions.find(
                  (option) => option.value === carService.filter.percent_id
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    percent_id: selectedOption ? selectedOption.value : 0,
                    page: 1,
                  }));
                }}
                isClearable
                placeholder={t('Procent')}
              />
            </th>
          )}

          {app.hasPermission("CLIENT_DISCOUNT") && columns.client_discount && (
            <th>{t('CompanyDiscount')}</th>
          )}

          {columns.status && app.hasPermission("STATUS_SHOW") && (
            <th className={css.pad0}>
              <Select
                styles={selectStyle}
                options={statuses}
                value={statuses.find(
                  (option) => option.value === carService.filter.status_id
                )}
                onChange={(selectedOption) => {
                  carService.setFilter((prevState) => ({
                    ...prevState,
                    status_id: selectedOption ? selectedOption.value : 0,
                    page: 1,
                  }));
                }}
                isClearable
                placeholder={t('Status')} 
              />
            </th>
          )}

          {columns.doc_issue && app.hasPermission("DOC_ISSUER_SHOW") && (
            <th>{t('Recipientofthedocument')}</th>
          )}

          {columns.final_recipient &&
            app.hasPermission("FINAL_RECIPIENT_SHOW") && (
              <th>{t('FinalRecipient')}</th>
            )}

          {app.hasPermission("PWIN_SHOW") && <th>{t('PlannedProfit')}</th>}
          {app.hasPermission("RWIN_SHOW") && <th>{t('RealProfit')}</th>}

          {columns.archive && app.hasPermission("ARCHIVE_UPDATE") && (
            <th>{t('Archive')}</th>
          )}

          {columns.feedback && app.hasPermission("FEEDBACK_SHOW") && (
            <th>{t('Feedback')}</th>
          )}
          <th
            className={css.pad0 + " " + css.id + " --- " + css.stickyColLast}
            style={{
              zIndex: "999",
              position: "sticky",
              left: "33px",
              backgroundColor: "#f2f2f2",
            }}
          ></th>
        </tr>
      </thead>
    );
  }
};

export default CarSearch;
